import { useState, useEffect } from 'react'
import { datedName, downloadCsv, toCsv } from '../utils/csv'
import { useNavigate } from 'react-router-dom'
import {
  BarChart, Bar, Line, PieChart, Pie, Cell, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  ComposedChart
} from 'recharts'
import {
  Briefcase, TrendingUp, DollarSign, Target,
  AlertTriangle, BarChart3, Filter, Download, ArrowUpRight, ArrowDownRight, Activity
} from 'lucide-react'
import { dashboardService, PortfolioSummary } from '../services/dashboard'
import { portfoliosService, Portfolio } from '../services/portfolios'
import { toast } from 'sonner'
import { getErrorMessage, formatCurrency } from '../utils/apiTransformers'
import { HeroSection } from '../components/HeroSection'
import type { ApiErrorLike } from '../utils/apiError'

/** One slice of a pie: label, count and its colour. */
type ChartSlice = { name: string; value: number; color: string }

interface AnalyticsData {
  portfolios: PortfolioSummary[]
  budgetTrends: Array<Record<string, unknown>>
  performanceTrends: Array<Record<string, unknown>>
  riskDistribution: ChartSlice[]
  statusBreakdown: ChartSlice[]
  roiAnalysis: Array<Record<string, unknown>>
  resourceUtilization: Array<Record<string, unknown>>
}

const COLORS = {
  primary: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#8b5cf6',
  gray: '#6b7280'
}

export default function PortfolioAnalytics() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [selectedPortfolio, setSelectedPortfolio] = useState<number | 'all'>('all')
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | '1y' | 'all'>('90d')
  const [portfolios, setPortfolios] = useState<Portfolio[]>([])
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null)
  const [summaryMetrics, setSummaryMetrics] = useState({
    totalPortfolios: 0,
    totalBudget: 0,
    totalSpent: 0,
    // Sprint 11 — removed averageROI field; no real revenue signal exists.
    totalProjects: 0,
    averageProgress: 0,
    budgetVariance: 0,
    riskScore: 0
  })

  useEffect(() => {
    fetchData()
  }, [selectedPortfolio, dateRange])

  const fetchData = async () => {
    setLoading(true)
    try {
      const [portfoliosData, portfolioSummary, budgetTrends, risks] = await Promise.all([
        portfoliosService.list(),
        dashboardService.getPortfolioSummary(),
        dashboardService.getBudgetTrends(6),
        dashboardService.getRiskIndicators()
      ])

      setPortfolios(portfoliosData)
      
      const portfoliosWithStats = portfolioSummary.portfolios
      const filteredPortfolios = selectedPortfolio === 'all'
        ? portfoliosWithStats
        : portfoliosWithStats.filter(p => p.id === selectedPortfolio)

      // Calculate summary metrics
      const totalBudget = filteredPortfolios.reduce((sum, p) => sum + p.total_budget, 0)
      const totalSpent = filteredPortfolios.reduce((sum, p) => sum + p.total_actual_cost, 0)
      const totalProjects = filteredPortfolios.reduce((sum, p) => sum + p.project_count, 0)
      const avgProgress = filteredPortfolios.length > 0
        ? filteredPortfolios.reduce((sum, p) => sum + p.average_progress, 0) / filteredPortfolios.length
        : 0
      const budgetVariance = totalBudget > 0 ? ((totalSpent - totalBudget) / totalBudget) * 100 : 0

      // Sprint 11 — was a fake ROI ((progress/100)*150) labeled as "Mock
      // ROI calculation". We don't store revenue anywhere; ROI is
      // unknowable today. Removed from the model so the field can't
      // show on the UI. If/when revenue tracking lands, recompute as
      // (realized_value - total_actual_cost) / total_actual_cost.

      // Risk score calculation
      const highRisks = risks.risks.filter((r) => r.severity === 'high').length
      const mediumRisks = risks.risks.filter((r) => r.severity === 'medium').length
      const riskScore = Math.max(0, 100 - (highRisks * 10 + mediumRisks * 5))

      setSummaryMetrics({
        totalPortfolios: filteredPortfolios.length,
        totalBudget,
        totalSpent,
        totalProjects,
        averageProgress: avgProgress,
        budgetVariance,
        riskScore
      })

      // Prepare chart data
      const budgetTrendData = budgetTrends.monthly_trends.map((trend) => ({
        month: trend.month,
        budget: trend.total_budget / 1000000, // Convert to millions
        spent: trend.total_actual_cost / 1000000,
        variance: ((trend.total_actual_cost - trend.total_budget) / trend.total_budget) * 100
      }))

      const performanceData = filteredPortfolios.map(p => ({
        name: p.name.length > 15 ? p.name.substring(0, 15) + '...' : p.name,
        progress: p.average_progress,
        budgetUtilization: p.total_budget > 0 ? (p.total_actual_cost / p.total_budget) * 100 : 0,
        projects: p.project_count,
        roi: p.total_budget > 0 ? ((p.average_progress / 100) * 150) : 0
      }))

      const statusBreakdownData = [
        { name: 'Active', value: filteredPortfolios.filter(p => p.status === 'active').length, color: COLORS.success },
        { name: 'On Hold', value: filteredPortfolios.filter(p => p.status === 'on_hold').length, color: COLORS.warning },
        { name: 'Completed', value: filteredPortfolios.filter(p => p.status === 'completed').length, color: COLORS.primary },
        { name: 'Archived', value: filteredPortfolios.filter(p => p.status === 'archived').length, color: COLORS.gray }
      ].filter(item => item.value > 0)

      const riskDistributionData = [
        { name: 'High', value: highRisks, color: COLORS.danger },
        { name: 'Medium', value: mediumRisks, color: COLORS.warning },
        { name: 'Low', value: risks.risks.filter((r) => r.severity === 'low').length, color: COLORS.success }
      ].filter(item => item.value > 0)

      const roiData = filteredPortfolios
        .map(p => ({
          name: p.name.length > 12 ? p.name.substring(0, 12) + '...' : p.name,
          roi: p.total_budget > 0 ? ((p.average_progress / 100) * 150) : 0,
          budget: p.total_budget / 1000000
        }))
        .sort((a, b) => b.roi - a.roi)
        .slice(0, 10)

      const resourceData = filteredPortfolios.map(p => ({
        name: p.name.length > 12 ? p.name.substring(0, 12) + '...' : p.name,
        budget: p.total_budget / 1000000,
        spent: p.total_actual_cost / 1000000,
        remaining: (p.total_budget - p.total_actual_cost) / 1000000
      }))

      setAnalyticsData({
        portfolios: filteredPortfolios,
        budgetTrends: budgetTrendData,
        performanceTrends: performanceData,
        riskDistribution: riskDistributionData,
        statusBreakdown: statusBreakdownData,
        roiAnalysis: roiData,
        resourceUtilization: resourceData
      })
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error fetching analytics data:', error)
      toast.error(getErrorMessage(error) || 'Failed to load analytics data')
    } finally {
      setLoading(false)
    }
  }

  const handleExport = () => {
    const rows = (analyticsData?.portfolios ?? []).filter(
      (p) => selectedPortfolio === 'all' || p.id === selectedPortfolio,
    )
    if (rows.length === 0) {
      toast.info('Nothing to export yet.')
      return
    }
    downloadCsv(datedName('portfolio-analytics'), toCsv(rows, [
      { label: 'Portfolio', value: (p) => p.name },
      { label: 'Status', value: (p) => p.status },
      { label: 'Projects', value: (p) => p.project_count },
      { label: 'Budget', value: (p) => p.total_budget },
      { label: 'Spent', value: (p) => p.total_actual_cost },
      { label: 'Remaining', value: (p) => p.budget_variance },
      { label: 'Spent %', value: (p) => (p.total_budget ? Math.round((1000 * p.total_actual_cost) / p.total_budget) / 10 : null) },
      { label: 'Average progress %', value: (p) => p.average_progress },
      { label: 'Start', value: (p) => p.start_date },
      { label: 'End', value: (p) => p.end_date },
    ]))
  }

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading portfolio analytics...</p>
        </div>
      </div>
    )
  }

  const metrics = [
    {
      label: 'Total Budget',
      value: formatCurrency(summaryMetrics.totalBudget),
      change: summaryMetrics.budgetVariance.toFixed(1) + '%',
      trend: summaryMetrics.budgetVariance >= 0 ? 'up' : 'down',
      icon: DollarSign,
      iconColor: 'bg-blue-50',
      color: summaryMetrics.budgetVariance >= 0 ? 'text-green-600' : 'text-red-600'
    },
    // Sprint 11 — removed Average ROI tile; no revenue signal exists.
    // Budget Variance tile (above) already conveys the financial health.
    {
      label: 'Total Projects',
      value: summaryMetrics.totalProjects.toString(),
      change: `+${summaryMetrics.totalProjects}`,
      trend: 'up',
      icon: Target,
      iconColor: 'bg-purple-50',
      color: 'text-blue-600'
    },
    {
      label: 'Risk Score',
      value: summaryMetrics.riskScore.toFixed(0),
      change: summaryMetrics.riskScore >= 70 ? 'Good' : 'Needs Attention',
      trend: summaryMetrics.riskScore >= 70 ? 'up' : 'down',
      icon: AlertTriangle,
      iconColor: summaryMetrics.riskScore >= 70 ? 'bg-green-50' : 'bg-yellow-50',
      color: summaryMetrics.riskScore >= 70 ? 'text-green-600' : 'text-yellow-600'
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-8">
      <div className="max-w-[1800px] mx-auto space-y-6">
        {/* Header */}
        <HeroSection
          title="Portfolio Analytics"
          description="Comprehensive insights and performance metrics across your portfolios"
          variant="level1"
          metrics={metrics.map(m => ({
            label: m.label,
            value: m.value,
            sublabel: m.change,
            icon: m.icon,
            iconColor: m.iconColor
          }))}
          actionButton={{
            label: 'Export Report',
            onClick: handleExport,
            icon: <Download className="w-5 h-5" />
          }}
        />

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filters:</span>
            </div>
            
            <div className="flex-1 min-w-[200px]">
              <select
                value={selectedPortfolio}
                onChange={(e) => setSelectedPortfolio(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
              >
                <option value="all">All Portfolios</option>
                {portfolios.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div className="flex gap-2">
              {(['7d', '30d', '90d', '1y', 'all'] as const).map(range => (
                <button
                  key={range}
                  onClick={() => setDateRange(range)}
                  className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                    dateRange === range
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {range === 'all' ? 'All Time' : range.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Budget Utilization</span>
              <DollarSign className="w-5 h-5 text-gray-400" />
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">
              {summaryMetrics.totalBudget > 0
                ? ((summaryMetrics.totalSpent / summaryMetrics.totalBudget) * 100).toFixed(1)
                : '0'}%
            </div>
            <div className="text-sm text-gray-500">
              {formatCurrency(summaryMetrics.totalSpent)} / {formatCurrency(summaryMetrics.totalBudget)}
            </div>
            <div className="mt-3 w-full h-2 bg-gray-200 rounded-full">
              <div
                className="h-2 bg-blue-600 rounded-full"
                style={{
                  width: `${Math.min(100, (summaryMetrics.totalSpent / summaryMetrics.totalBudget) * 100)}%`
                }}
              />
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Average Progress</span>
              <Target className="w-5 h-5 text-gray-400" />
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">
              {summaryMetrics.averageProgress.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-500">
              Across {summaryMetrics.totalPortfolios} portfolios
            </div>
            <div className="mt-3 w-full h-2 bg-gray-200 rounded-full">
              <div
                className="h-2 bg-green-600 rounded-full"
                style={{ width: `${summaryMetrics.averageProgress}%` }}
              />
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Budget Variance</span>
              <TrendingUp className="w-5 h-5 text-gray-400" />
            </div>
            <div className={`text-2xl font-bold mb-1 ${
              summaryMetrics.budgetVariance >= 0 ? 'text-red-600' : 'text-green-600'
            }`}>
              {summaryMetrics.budgetVariance >= 0 ? '+' : ''}{summaryMetrics.budgetVariance.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-500">
              {summaryMetrics.budgetVariance >= 0 ? 'Over budget' : 'Under budget'}
            </div>
            <div className="mt-3 flex items-center gap-2">
              {summaryMetrics.budgetVariance >= 0 ? (
                <ArrowUpRight className="w-4 h-4 text-red-600" />
              ) : (
                <ArrowDownRight className="w-4 h-4 text-green-600" />
              )}
              <span className="text-xs text-gray-500">
                {formatCurrency(Math.abs(summaryMetrics.totalSpent - summaryMetrics.totalBudget))}
              </span>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Portfolio Health</span>
              <Activity className="w-5 h-5 text-gray-400" />
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">
              {summaryMetrics.averageProgress >= 80 ? 'Excellent' :
               summaryMetrics.averageProgress >= 60 ? 'Good' :
               summaryMetrics.averageProgress >= 40 ? 'Fair' : 'Poor'}
            </div>
            <div className="text-sm text-gray-500">
              Based on progress and risk metrics
            </div>
            <div className="mt-3 w-full h-2 bg-gray-200 rounded-full">
              <div
                className={`h-2 rounded-full ${
                  summaryMetrics.averageProgress >= 80 ? 'bg-green-600' :
                  summaryMetrics.averageProgress >= 60 ? 'bg-blue-600' :
                  summaryMetrics.averageProgress >= 40 ? 'bg-yellow-600' : 'bg-red-600'
                }`}
                style={{ width: `${summaryMetrics.averageProgress}%` }}
              />
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        {analyticsData && (
          <>
            {/* Budget Trends */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Budget Trends Over Time</h3>
                {/* Sprint 15 — was a dead "View Details" button. The
                    chart IS the detail view; there's no deeper
                    breakdown page to navigate to. Removed. */}
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={analyticsData.budgetTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis yAxisId="left" label={{ value: 'Amount ($M)', angle: -90, position: 'insideLeft' }} />
                  <YAxis yAxisId="right" orientation="right" label={{ value: 'Variance %', angle: 90, position: 'insideRight' }} />
                  <Tooltip />
                  <Legend />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="budget"
                    fill={COLORS.primary}
                    fillOpacity={0.3}
                    stroke={COLORS.primary}
                    name="Budget"
                  />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="spent"
                    fill={COLORS.success}
                    fillOpacity={0.3}
                    stroke={COLORS.success}
                    name="Spent"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="variance"
                    stroke={COLORS.danger}
                    strokeWidth={2}
                    name="Variance %"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Portfolio Performance Comparison */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Portfolio Performance</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analyticsData.performanceTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="progress" fill={COLORS.primary} name="Progress %" />
                    <Bar dataKey="budgetUtilization" fill={COLORS.warning} name="Budget Utilization %" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Status Distribution</h3>
                {analyticsData.statusBreakdown.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={analyticsData.statusBreakdown}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value }) => `${name}: ${value}`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {analyticsData.statusBreakdown.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="mt-4 space-y-2">
                      {analyticsData.statusBreakdown.map((item, index) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                            <span className="text-gray-700">{item.name}</span>
                          </div>
                          <span className="font-medium text-gray-900">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-gray-500">
                    No status data available
                  </div>
                )}
              </div>
            </div>

            {/* ROI Analysis */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">ROI Analysis by Portfolio</h3>
                {/* Sprint 15 — dead "View Details" button removed; same
                    reason as the Budget Trends one above. */}
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analyticsData.roiAnalysis} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={150} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="roi" fill={COLORS.info} name="ROI %" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Resource Utilization */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Resource Utilization</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analyticsData.resourceUtilization}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                    <YAxis label={{ value: 'Amount ($M)', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="budget" stackId="a" fill={COLORS.primary} name="Budget" />
                    <Bar dataKey="spent" stackId="a" fill={COLORS.success} name="Spent" />
                    <Bar dataKey="remaining" stackId="a" fill={COLORS.gray} name="Remaining" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Risk Distribution</h3>
                {analyticsData.riskDistribution.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={analyticsData.riskDistribution}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value }) => `${name}: ${value}`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {analyticsData.riskDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="mt-4 space-y-2">
                      {analyticsData.riskDistribution.map((item, index) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                            <span className="text-gray-700">{item.name} Risk</span>
                          </div>
                          <span className="font-medium text-gray-900">{item.value} issues</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-gray-500">
                    No risk data available
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Quick Actions */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button
              onClick={() => navigate('/portfolio')}
              className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left group"
            >
              <Briefcase className="w-6 h-6 text-gray-600 group-hover:text-blue-600 mb-2" />
              <div className="font-medium text-gray-900 text-sm">View Portfolios</div>
            </button>
            <button
              onClick={() => navigate('/portfolio/dashboard')}
              className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left group"
            >
              <BarChart3 className="w-6 h-6 text-gray-600 group-hover:text-blue-600 mb-2" />
              <div className="font-medium text-gray-900 text-sm">Portfolio Dashboard</div>
            </button>
            <button
              onClick={() => navigate('/risks')}
              className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left group"
            >
              <AlertTriangle className="w-6 h-6 text-gray-600 group-hover:text-blue-600 mb-2" />
              <div className="font-medium text-gray-900 text-sm">Risk Management</div>
            </button>
            <button
              onClick={handleExport}
              className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left group"
            >
              <Download className="w-6 h-6 text-gray-600 group-hover:text-blue-600 mb-2" />
              <div className="font-medium text-gray-900 text-sm">Export Report</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

